import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Eye, Waves, Vibrate } from 'lucide-react';

const InteractiveFeatureShowcase = () => {
  const [activeFeature, setActiveFeature] = useState('detection');

  const features = {
    detection: {
      icon: <Eye className="w-6 h-6" />,
      title: 'Obstacle Detection',
      description: 'Forward-facing sensors scan your path continuously',
      color: 'blue',
    },
    surface: {
      icon: <Waves className="w-6 h-6" />,
      title: 'Surface Analysis',
      description: 'Ground sensors detect drops, potholes, and steps',
      color: 'purple',
    },
    feedback: {
      icon: <Vibrate className="w-6 h-6" />,
      title: 'Haptic Feedback',
      description: 'Intuitive vibration patterns alert you to hazards',
      color: 'emerald',
    },
  };

  const colorMap = {
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/40', text: 'text-blue-400', fill: '#3b82f6' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/40', text: 'text-purple-400', fill: '#a855f7' },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', text: 'text-emerald-400', fill: '#10b981' },
  };

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      {/* Animated Visualization */}
      <div className="relative aspect-square max-w-md mx-auto order-2 lg:order-1">
        <div className="relative w-full h-full rounded-3xl overflow-hidden bg-gradient-to-br from-gray-800/30 to-gray-900/50 border border-gray-800/50">
          <svg viewBox="0 0 400 500" className="w-full h-full">
            <defs>
              <linearGradient id="stickGradFeature" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4a4a4a" />
                <stop offset="100%" stopColor="#2a2a2a" />
              </linearGradient>
              <filter id="featureGlow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Environment - Ground */}
            <rect x="0" y="420" width="400" height="80" fill="#1a1a1a" />
            <line x1="0" y1="420" x2="400" y2="420" stroke="#2a2a2a" strokeWidth="2" />

            {/* Walking path marks */}
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <rect key={i} x={50 + i * 60} y="430" width="40" height="3" rx="1" fill="#2a2a2a" />
            ))}

            {/* Pothole (for surface detection) */}
            <AnimatePresence>
              {activeFeature === 'surface' && (
                <motion.g
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <ellipse cx="280" cy="425" rx="30" ry="8" fill="#0f0f0f" />
                  <motion.ellipse
                    cx="280"
                    cy="425"
                    rx="35"
                    ry="10"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="2"
                    strokeDasharray="5 3"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <text x="280" y="450" textAnchor="middle" fill="#ef4444" fontSize="10">HAZARD</text>
                </motion.g>
              )}
            </AnimatePresence>

            {/* Obstacle (for detection) */}
            <AnimatePresence>
              {activeFeature === 'detection' && (
                <motion.g
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                >
                  <rect x="280" y="330" width="40" height="90" rx="4" fill="#2a2a2a" />
                  <rect x="285" y="335" width="30" height="5" rx="2" fill="#3a3a3a" />
                  <motion.rect
                    x="275"
                    y="325"
                    width="50"
                    height="100"
                    rx="6"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <text x="300" y="315" textAnchor="middle" fill="#f59e0b" fontSize="10">OBSTACLE</text>
                </motion.g>
              )}
            </AnimatePresence>

            {/* User silhouette */}
            <g transform="translate(80, 200)">
              {/* Head */}
              <circle cx="40" cy="20" r="20" fill="#3a3a3a" />
              {/* Body */}
              <ellipse cx="40" cy="80" rx="25" ry="40" fill="#3a3a3a" />
              {/* Arm holding stick */}
              <path d="M 55 60 Q 80 80 100 120" stroke="#3a3a3a" strokeWidth="12" strokeLinecap="round" fill="none" />
            </g>

            {/* Walking stick with module */}
            <g transform="translate(170, 150)">
              {/* Stick */}
              <rect x="0" y="0" width="12" height="270" rx="6" fill="url(#stickGradFeature)" />
              
              {/* Module */}
              <rect x="-10" y="30" width="32" height="45" rx="6" fill="#2a2a2a" stroke="#3a3a3a" strokeWidth="1" />
              
              {/* Ultrasonic sensors */}
              <circle cx="0" cy="45" r="5" fill="#1a1a1a" stroke={activeFeature === 'detection' ? '#3b82f6' : '#3a3a3a'} strokeWidth="1.5" />
              <circle cx="12" cy="45" r="5" fill="#1a1a1a" stroke={activeFeature === 'detection' ? '#3b82f6' : '#3a3a3a'} strokeWidth="1.5" />
              
              {/* IR sensor */}
              <rect x="-5" y="65" width="22" height="6" rx="2" fill="#1a1a1a" stroke={activeFeature === 'surface' ? '#a855f7' : '#3a3a3a'} strokeWidth="1.5" />
              
              {/* Status LED */}
              <motion.circle
                cx="6"
                cy="55"
                r="3"
                fill={activeFeature === 'feedback' ? '#10b981' : '#3a3a3a'}
                filter={activeFeature === 'feedback' ? 'url(#featureGlow)' : 'none'}
                animate={activeFeature === 'feedback' ? { opacity: [0.5, 1, 0.5] } : {}}
                transition={{ duration: 0.3, repeat: Infinity }}
              />

              {/* Detection visualization */}
              <AnimatePresence>
                {activeFeature === 'detection' && (
                  <motion.g
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {[1, 2, 3, 4].map((i) => (
                      <motion.path
                        key={i}
                        d={`M 6 45 Q ${50 + i * 20} ${20 - i * 10} ${100 + i * 15} ${60 + i * 20}`}
                        fill="none"
                        stroke="rgba(59, 130, 246, 0.4)"
                        strokeWidth="2"
                        animate={{ opacity: [0.1, 0.5, 0.1] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.15 }}
                      />
                    ))}
                  </motion.g>
                )}
              </AnimatePresence>

              {/* Surface detection beam */}
              <AnimatePresence>
                {activeFeature === 'surface' && (
                  <motion.g
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.polygon
                      points="6,75 -30,270 42,270"
                      fill="rgba(168, 85, 247, 0.2)"
                      animate={{ opacity: [0.2, 0.5, 0.2] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                    <motion.ellipse
                      cx="6"
                      cy="270"
                      rx="40"
                      ry="10"
                      fill="none"
                      stroke="rgba(168, 85, 247, 0.6)"
                      strokeWidth="2"
                      animate={{ rx: [40, 45, 40] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  </motion.g>
                )}
              </AnimatePresence>

              {/* Haptic feedback visualization */}
              <AnimatePresence>
                {activeFeature === 'feedback' && (
                  <motion.g
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {[1, 2, 3].map((i) => (
                      <motion.circle
                        key={i}
                        cx="6"
                        cy="55"
                        r={10 + i * 8}
                        fill="none"
                        stroke="rgba(16, 185, 129, 0.4)"
                        strokeWidth="2"
                        initial={{ scale: 0.5, opacity: 1 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{
                          duration: 1.2,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                      />
                    ))}
                  </motion.g>
                )}
              </AnimatePresence>
            </g>
          </svg>
        </div>
      </div>

      {/* Feature Controls */}
      <div className="space-y-6 order-1 lg:order-2">
        <div>
          <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-4">
            How It Helps
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
            Navigate with confidence
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            ClearWay provides the awareness you need to move through the world on your own terms — 
            detecting obstacles before you reach them and alerting you to changes in terrain.
          </p>
        </div>

        <div className="space-y-3">
          {Object.entries(features).map(([key, feature]) => {
            const colors = colorMap[feature.color];
            const isActive = activeFeature === key;

            return (
              <motion.button
                key={key}
                onClick={() => setActiveFeature(key)}
                className={`w-full p-5 rounded-xl border text-left transition-all ${
                  isActive
                    ? `${colors.bg} ${colors.border}`
                    : 'bg-gray-800/30 border-gray-700/50 hover:border-gray-600'
                }`}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isActive ? colors.bg : 'bg-gray-800/50'
                  } ${isActive ? colors.text : 'text-gray-500'}`}>
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-medium mb-1 ${isActive ? 'text-white' : 'text-gray-300'}`}>
                      {feature.title}
                    </h4>
                    <p className="text-gray-500 text-sm">{feature.description}</p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="flex items-center gap-3 pt-4">
          <Shield className="w-5 h-5 text-blue-400" />
          <p className="text-gray-500 text-sm">Proactive safety through intelligent sensing</p>
        </div>
      </div>
    </div>
  );
};

export default InteractiveFeatureShowcase;
